import React, { useState, useEffect, useCallback } from 'react';
import { Banknote, Plus, Edit2, Trash2, X, Check, AlertCircle, Loader2, Search, DollarSign, Eye } from 'lucide-react';
import financeService from '../services/finance';
import DeleteConfirmModal from './DeleteConfirmModal';
import PaginationBar from './PaginationBar';
import usePagination from '../hooks/usePagination';

const TIPE_AKUN = ['ASET', 'KEWAJIBAN', 'MODAL', 'PENDAPATAN', 'BEBAN', 'LAINNYA'];
const KATEGORI = ['KAS', 'BANK', 'PIUTANG', 'HUTANG', 'MODAL', 'PENDAPATAN', 'BEBAN_OPERASIONAL', 'BEBAN_GAJI', 'BEBAN_LAIN', 'LAINNYA'];

const formatRupiah = (num) => {
  return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(num || 0);
};

const MasterAkun = () => {
  const [akunList, setAkunList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTipe, setFilterTipe] = useState('All');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({ kodeAkun: '', namaAkun: '', tipeAkun: 'LAINNYA', kategori: 'LAINNYA', saldoAwal: 0, deskripsi: '' });
  const [formError, setFormError] = useState('');
  const [formLoading, setFormLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [viewDetail, setViewDetail] = useState(null);

  // filteredAkun harus sebelum usePagination untuk hindari Temporal Dead Zone
  const filteredAkun = React.useMemo(() => {
    if (!akunList.length) return [];
    return akunList.filter(a => {
      if (filterTipe !== 'All' && a.tipeAkun !== filterTipe) return false;
      if (searchTerm) {
        const s = searchTerm.toLowerCase();
        return a.namaAkun.toLowerCase().includes(s) || a.kodeAkun.toLowerCase().includes(s);
      }
      return true;
    });
  }, [akunList, filterTipe, searchTerm]);

  const { paginatedItems, currentPage, totalPages, totalItems, startIndex, endIndex, goToPage } = usePagination(filteredAkun, 10);

  const fetchAkun = useCallback(async () => {
    try {
      setLoading(true);
      const data = await financeService.getAkun({ isActive: 'true' });
      setAkunList(data.akun || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAkun(); }, [fetchAkun]);

  const showSuccess = (msg) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const resetForm = () => {
    setFormData({ kodeAkun: '', namaAkun: '', tipeAkun: 'LAINNYA', kategori: 'LAINNYA', saldoAwal: 0, deskripsi: '' });
    setFormError('');
    setEditing(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!formData.kodeAkun || !formData.namaAkun) {
      setFormError('Kode Akun dan Nama Akun wajib diisi');
      return;
    }
    setFormLoading(true);
    try {
      if (editing) {
        await financeService.updateAkun(editing._id, formData);
        showSuccess('Akun updated successfully');
      } else {
        await financeService.createAkun(formData);
        showSuccess('Akun created successfully');
      }
      await fetchAkun();
      setShowModal(false);
      resetForm();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm || isDeleting) return;
    setIsDeleting(true);
    try {
      await financeService.deleteAkun(deleteConfirm._id);
      setDeleteConfirm(null);
      await fetchAkun();
      showSuccess('Akun deleted');
    } catch (err) {
      setError(err.message);
      setDeleteConfirm(null);
    } finally {
      setIsDeleting(false);
    }
  };

  const getTipeColor = (tipe) => {
    const colors = {
      ASET: 'bg-blue-100 text-blue-700 border-blue-200',
      KEWAJIBAN: 'bg-orange-100 text-orange-700 border-orange-200',
      MODAL: 'bg-purple-100 text-purple-700 border-purple-200',
      PENDAPATAN: 'bg-green-100 text-green-700 border-green-200',
      BEBAN: 'bg-red-100 text-red-700 border-red-200',
      LAINNYA: 'bg-gray-100 text-gray-700 border-gray-200',
    };
    return colors[tipe] || colors.LAINNYA;
  };

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Master Akun</h2>
          <p className="mt-1 text-sm text-gray-500">Manage finance accounts (CoA - Chart of Accounts)</p>
        </div>
        <button onClick={() => { resetForm(); setShowModal(true); }} className="btn-primary text-sm">
          <Plus className="w-4 h-4" /><span>Add Akun</span>
        </button>
      </div>

      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 animate-slide-up">
          <div className="flex items-center space-x-2"><Check className="w-5 h-5 text-green-500" /><p className="text-sm font-medium text-green-800">{successMessage}</p></div>
        </div>
      )}

      <div className="card">
        <div className="px-4 py-3 space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input type="text" placeholder="Search by kode or nama akun..." value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-8 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 bg-gray-50 hover:bg-white focus:bg-white" />
          </div>
          <select value={filterTipe} onChange={e => setFilterTipe(e.target.value)} className="select-field text-sm">
            <option value="All">All Types</option>
            {TIPE_AKUN.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">Total <span className="font-semibold text-gray-700">{totalItems}</span> akun</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {paginatedItems.map(akun => (
          <div key={akun._id} className="card p-4 hover:shadow-md transition-all">
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                  <Banknote className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="flex items-center space-x-2">
                    <span className="text-xs font-mono text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">{akun.kodeAkun}</span>
                    <span className={`inline-flex px-2 py-0.5 text-[10px] font-medium rounded-full border ${getTipeColor(akun.tipeAkun)}`}>{akun.tipeAkun}</span>
                  </div>
                  <h3 className="text-sm font-semibold text-gray-900 mt-1">{akun.namaAkun}</h3>
                  {akun.deskripsi && <p className="text-xs text-gray-400 mt-0.5">{akun.deskripsi}</p>}
                  <div className="flex items-center space-x-3 mt-2">
                    <span className="text-xs text-gray-500">Saldo Awal: <strong className="text-gray-700">{formatRupiah(akun.saldoAwal)}</strong></span>
                    <span className="text-xs text-gray-500">Saldo: <strong className={akun.saldoSaatIni >= 0 ? 'text-green-600' : 'text-red-600'}>{formatRupiah(akun.saldoSaatIni)}</strong></span>
                  </div>
                </div>
              </div>
              <div className="flex space-x-1">
                <button onClick={() => setViewDetail(akun)}
                  className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg" title="Lihat detail"><Eye className="w-4 h-4" /></button>
                <button onClick={() => { setEditing(akun); setFormData({ kodeAkun: akun.kodeAkun, namaAkun: akun.namaAkun, tipeAkun: akun.tipeAkun, kategori: akun.kategori, saldoAwal: akun.saldoAwal, deskripsi: akun.deskripsi || '' }); setShowModal(true); }}
                  className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg"><Edit2 className="w-4 h-4" /></button>
                <button onClick={() => setDeleteConfirm(akun)}
                  className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          </div>
        ))}
        {filteredAkun.length === 0 && (
          <div className="col-span-2 card p-12 text-center">
            <Banknote className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">No akun found</p>
          </div>
        )}
      </div>

      <PaginationBar currentPage={currentPage} totalPages={totalPages} totalItems={totalItems} startIndex={startIndex} endIndex={endIndex} goToPage={goToPage} />

      {/* View Detail Modal */}
      {viewDetail && (
        <div className="fixed inset-0 z-50 overflow-y-auto" onClick={() => setViewDetail(null)}>
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
            <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full z-10 animate-scale-in" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
                <div className="flex items-center space-x-3">
                  <div className="p-2.5 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white">
                    <Banknote className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">Detail Akun</h3>
                    <p className="text-sm text-gray-500">Informasi lengkap akun keuangan</p>
                  </div>
                </div>
                <button onClick={() => setViewDetail(null)} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="px-6 py-5 space-y-4">
                <div className="flex items-center space-x-4 pb-4 border-b border-gray-100">
                  <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                    <Banknote className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <div className="flex items-center space-x-2">
                      <span className="text-xs font-mono text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">{viewDetail.kodeAkun}</span>
                      <span className={`inline-flex px-2 py-0.5 text-[10px] font-medium rounded-full border ${
                        {ASET: 'bg-blue-100 text-blue-700 border-blue-200', KEWAJIBAN: 'bg-orange-100 text-orange-700 border-orange-200', MODAL: 'bg-purple-100 text-purple-700 border-purple-200', PENDAPATAN: 'bg-green-100 text-green-700 border-green-200', BEBAN: 'bg-red-100 text-red-700 border-red-200', LAINNYA: 'bg-gray-100 text-gray-700 border-gray-200'}[viewDetail.tipeAkun] || 'bg-gray-100 text-gray-700 border-gray-200'
                      }`}>{viewDetail.tipeAkun}</span>
                    </div>
                    <h4 className="text-xl font-bold text-gray-900 mt-1">{viewDetail.namaAkun}</h4>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-xl p-4">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Kategori</label>
                    <p className="text-sm font-semibold text-gray-900">{viewDetail.kategori || '-'}</p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Saldo Awal</label>
                    <p className="text-sm font-bold text-gray-900">{formatRupiah(viewDetail.saldoAwal || 0)}</p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Saldo Saat Ini</label>
                    <p className={`text-sm font-bold ${viewDetail.saldoSaatIni >= 0 ? 'text-green-600' : 'text-red-600'}`}>{formatRupiah(viewDetail.saldoSaatIni)}</p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
                    <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full ${viewDetail.isActive !== false ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${viewDetail.isActive !== false ? 'bg-green-500' : 'bg-red-500'}`} />
                      {viewDetail.isActive !== false ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>

                {viewDetail.deskripsi && (
                  <div className="bg-gray-50 rounded-xl p-4">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Deskripsi</label>
                    <p className="text-sm font-semibold text-gray-900">{viewDetail.deskripsi}</p>
                  </div>
                )}

                {viewDetail.createdAt && (
                  <div className="bg-gray-50 rounded-xl p-4">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Dibuat pada</label>
                    <p className="text-sm font-semibold text-gray-900">{new Date(viewDetail.createdAt).toLocaleDateString('id-ID', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                  </div>
                )}

                <div className="flex justify-end pt-2">
                  <button onClick={() => setViewDetail(null)} className="btn-secondary text-sm">
                    <X className="w-4 h-4" /><span>Tutup</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <DeleteConfirmModal
        isOpen={deleteConfirm !== null}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={handleDeleteConfirm}
        title="Hapus Akun"
        isLoading={isDeleting}
      >
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500 font-medium">Kode Akun</span>
          <span className="text-xs font-mono font-bold text-gray-900">{deleteConfirm?.kodeAkun}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500 font-medium">Nama Akun</span>
          <span className="text-sm font-semibold text-gray-900 text-right max-w-[200px]">{deleteConfirm?.namaAkun}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500 font-medium">Tipe</span>
          <span className={`inline-flex px-2.5 py-1 text-[10px] font-medium rounded-full border ${
            {ASET: 'bg-blue-100 text-blue-700 border-blue-200', KEWAJIBAN: 'bg-orange-100 text-orange-700 border-orange-200', MODAL: 'bg-purple-100 text-purple-700 border-purple-200', PENDAPATAN: 'bg-green-100 text-green-700 border-green-200', BEBAN: 'bg-red-100 text-red-700 border-red-200', LAINNYA: 'bg-gray-100 text-gray-700 border-gray-200'}[deleteConfirm?.tipeAkun] || 'bg-gray-100 text-gray-700 border-gray-200'
          }`}>{deleteConfirm?.tipeAkun}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500 font-medium">Kategori</span>
          <span className="text-xs font-semibold text-gray-900">{deleteConfirm?.kategori || '-'}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500 font-medium">Saldo Awal</span>
          <span className="text-xs font-bold text-gray-900">{formatRupiah(deleteConfirm?.saldoAwal || 0)}</span>
        </div>
        {deleteConfirm?.deskripsi && (
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500 font-medium">Deskripsi</span>
            <span className="text-xs font-semibold text-gray-900 text-right max-w-[200px]">{deleteConfirm.deskripsi}</span>
          </div>
        )}
      </DeleteConfirmModal>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto" onClick={() => { setShowModal(false); resetForm(); }}>
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
            <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full z-10 animate-scale-in" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between px-6 py-5 border-b">
                <div className="flex items-center space-x-3">
                  <div className="p-2.5 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white"><Banknote className="w-5 h-5" /></div>
                  <div><h3 className="text-lg font-bold text-gray-900">{editing ? 'Edit Akun' : 'Add Akun'}</h3></div>
                </div>
                <button onClick={() => { setShowModal(false); resetForm(); }} className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg"><X className="w-5 h-5" /></button>
              </div>
              <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
                {formError && <div className="p-3 bg-red-50 border border-red-200 rounded-xl"><AlertCircle className="w-5 h-5 text-red-500 inline-block mr-2" />{formError}</div>}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Kode Akun <span className="text-red-500">*</span></label>
                    <input type="text" value={formData.kodeAkun} onChange={e => setFormData(p => ({ ...p, kodeAkun: e.target.value }))}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 bg-gray-50 focus:bg-white" placeholder="e.g. 101" disabled={!!editing} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Saldo Awal</label>
                    <input type="number" value={formData.saldoAwal} onChange={e => setFormData(p => ({ ...p, saldoAwal: Number(e.target.value) }))}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 bg-gray-50 focus:bg-white" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Nama Akun <span className="text-red-500">*</span></label>
                  <input type="text" value={formData.namaAkun} onChange={e => setFormData(p => ({ ...p, namaAkun: e.target.value }))}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 bg-gray-50 focus:bg-white" placeholder="e.g. Kas Kecil" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Tipe Akun</label>
                    <select value={formData.tipeAkun} onChange={e => setFormData(p => ({ ...p, tipeAkun: e.target.value }))} className="select-field">
                      {TIPE_AKUN.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Kategori</label>
                    <select value={formData.kategori} onChange={e => setFormData(p => ({ ...p, kategori: e.target.value }))} className="select-field">
                      {KATEGORI.map(k => <option key={k} value={k}>{k.replace('_', ' ')}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Deskripsi</label>
                  <textarea value={formData.deskripsi} onChange={e => setFormData(p => ({ ...p, deskripsi: e.target.value }))} rows={2}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 bg-gray-50 focus:bg-white" />
                </div>
                <div className="flex justify-end space-x-3 pt-2">
                  <button type="button" onClick={() => { setShowModal(false); resetForm(); }} className="btn-secondary text-sm">Cancel</button>
                  <button type="submit" disabled={formLoading} className="btn-primary text-sm">
                    {formLoading ? <><Loader2 className="w-4 h-4 animate-spin" /><span>Saving...</span></> : <><Check className="w-4 h-4" /><span>{editing ? 'Update' : 'Create'}</span></>}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MasterAkun;
